// @ts-strict-ignore
import * as React from 'react';
import {
    AppleAppStoreData,
    AppleAppStoreDefaultSettings,
    AppPlatform,
    AppStoreAvailableBuild,
    AppStoreBuild,
    AppStoreReviewSubmissionItemState,
    AppStoreReviewSubmissionItemType,
    AppStoreReviewSubmissionState,
    AppStoreType,
    CIBuildInfo,
    IntegrationType,
    RegressionBuildInfo,
    RegressionStatus,
    ReleaseCandidateBuildInfo,
    ReleaseStepType,
    UnblockAppStoreReviewSubmissionState,
    ReleaseStepStatus,
    RunwayResponseType,
    QAHistoryItem,
    BuildMatchingWarning,
} from '@/types';
import { BuildAndReviewSettingsCard } from '@/components/AppStoreItems/BuildAndReviewSettingsCard';
import { AppStoreSubmissionCollapse } from '@/components/AppStoreItems/AppStoreSubmissionCollapse';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectReleasedAt,
    selectReleaseDisplayableVersion,
    selectIsWorkflowsMigratedAndEnabled,
    selectWorkflowEnabledSteps,
} from '@/selectors/selectors';
import Button from 'antd/lib/button';
import { AppstoreAddOutlined, ArrowRightOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import { AddReviewSubmissionItemsModal } from '@/components/StepContentSubmission/AddReviewSubmissionItemsModal';
import { renderReviewsSubmissionItemCard } from '@/components/StepContentSubmission/helpers';
import { ReleaseUpdatable } from '@/actions/updatable';
import { isAppSubmitted, isIosPlatform } from '@/helpers';
import { globalMessage } from '@/utils/globalMessage';
import { BaseUpdateReleaseRequest, tryUpdateRelease, UpdateReviewSubmissionItemRequest } from '@/actions/release';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { Nullable, StepRunComponentProps } from '@/typescriptHelpers';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { AddReviewSubmissionItemRequest, StepRunUpdatable } from '@/actions/stepRun';

export interface StepContentSubmissionIOSProps {
    orgId: string;
    appId: string;
    releaseStepType: ReleaseStepType;
    releaseId: string;
    appStoreData: AppleAppStoreData;
    appStoreReleaseSettings: AppleAppStoreDefaultSettings;
    isRollback: boolean;
    setEditReleaseSettingsModalVisible: (value: boolean) => void;
    isReleased: boolean;
    storeUrl: string;
    appStoreType: AppStoreType;
    platform: AppPlatform;
    displayableVersion: string;
    lastUploadedBuild?: AppStoreBuild;
    regressionBuildInfo?: RegressionBuildInfo;
    regressionStatus?: RegressionStatus;
    appStoreAvailableBuilds: Nullable<AppStoreAvailableBuild[]>;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    additionalAppStoreSelectedBuilds: Nullable<string[]>;
    latestSuccessfulProdBuild: Nullable<CIBuildInfo>;
    latestSuccessfulBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    latestPassingRegressionTestBuild: Nullable<QAHistoryItem>;
    stepStatus: ReleaseStepStatus;
    buildMatchingWarnings?: BuildMatchingWarning[];
}

export const StepContentSubmissionIOS: React.FC<StepContentSubmissionIOSProps> = ({
    orgId,
    appId,
    releaseStepType,
    releaseId,
    appStoreData,
    appStoreReleaseSettings,
    isRollback,
    setEditReleaseSettingsModalVisible,
    isReleased,
    storeUrl,
    appStoreType,
    platform,
    displayableVersion,
    lastUploadedBuild,
    regressionBuildInfo,
    regressionStatus,
    appStoreAvailableBuilds,
    appStoreSelectedBuilds,
    additionalAppStoreSelectedBuilds,
    latestSuccessfulBuild,
    latestSuccessfulProdBuild,
    selectedStagingBuild,
    stepRunComponentProps,
    latestPassingRegressionTestBuild,
    stepStatus,
    buildMatchingWarnings,
}) => {
    const dispatch = useDispatch();

    const unblockReviewSubmissionForReleaseStepLoading = useIsLoading(
        ReleaseUpdatable.UnblockReleaseReviewSubmission,
        releaseId,
    );
    const unblockReviewSubmissionForReleaseStepError = useError(
        ReleaseUpdatable.UnblockReleaseReviewSubmission,
        releaseId,
    );

    const [
        unblockReviewSubmissionStepRun,
        unblockReviewSubmissionIsLoadingStepRun,
        unblockReviewSubmissionErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.UnblockReviewSubmission, stepRunComponentProps);

    const unblockReviewSubmissionIsLoading =
        stepRunComponentProps ? unblockReviewSubmissionIsLoadingStepRun : unblockReviewSubmissionForReleaseStepLoading;
    const unblockReviewSubmissionError =
        stepRunComponentProps ? unblockReviewSubmissionErrorStepRun : unblockReviewSubmissionForReleaseStepError;

    const [
        addReviewSubmissionItemStepRun,
        addReviewSubmissionItemIsLoadingStepRun,
        addReviewSubmissionItemResponseTypeStepRun,
        addReviewSubmissionItemErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.AddReviewSubmissionItem, stepRunComponentProps);

    const addReviewSubmissionItemIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.AddReviewSubmissionItem,
        releaseId,
    );
    const addReviewSubmissionItemErrorReleaseStep = useError(ReleaseUpdatable.AddReviewSubmissionItem, releaseId);
    const addReviewSubmissionItemUpdatedReleaseStep = useUpdated(ReleaseUpdatable.AddReviewSubmissionItem, releaseId);

    const addReviewSubmissionItemIsLoading =
        stepRunComponentProps ? addReviewSubmissionItemIsLoadingStepRun : addReviewSubmissionItemIsLoadingReleaseStep;
    const addReviewSubmissionItemError =
        stepRunComponentProps ? addReviewSubmissionItemErrorStepRun : addReviewSubmissionItemErrorReleaseStep;
    const addReviewSubmissionItemSuccess =
        stepRunComponentProps ?
            addReviewSubmissionItemResponseTypeStepRun === RunwayResponseType.Success
        :   addReviewSubmissionItemUpdatedReleaseStep;

    const addReviewSubmissionItem = (itemId: string, category: AppStoreReviewSubmissionItemType) => {
        if (stepRunComponentProps) {
            const request: AddReviewSubmissionItemRequest = {
                itemId,
                type: category,
            };

            return addReviewSubmissionItemStepRun(request);
        }

        const request: UpdateReviewSubmissionItemRequest = {
            orgId,
            appId,
            releaseId,
            itemId,
            type: category,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.AddReviewSubmissionItem, request, releaseId));
    };

    React.useEffect(() => {
        if (addReviewSubmissionItemSuccess) {
            globalMessage.success('Review submission item added successfully!');
            setIsAddReviewSubmissionItemsModalVisible(false);
        }
    }, [addReviewSubmissionItemSuccess]);

    React.useEffect(() => {
        if (addReviewSubmissionItemError) {
            globalMessage.error(addReviewSubmissionItemError);
        }
    }, [addReviewSubmissionItemError]);

    const isAppVersionInsideReviewSubmission =
        !!appStoreData?.relevantReviewSubmission?.appStoreVersionForReviewId &&
        appStoreData?.relevantReviewSubmission?.appStoreVersionForReviewId ===
            appStoreData?.versionReviewSubmissionItem?.id;

    const [isAddReviewSubmissionItemsModalVisible, setIsAddReviewSubmissionItemsModalVisible] = React.useState(false);

    const selectedBuilds = appStoreSelectedBuilds;

    const ASCLink = appStoreData?.relevantReviewSubmission?.reviewSubmissionASCLink;

    const releaseDisplayableVersion = useSelector((state: RootState) =>
        selectReleaseDisplayableVersion(state, releaseId),
    );

    const isSubmitted = isAppSubmitted(
        appStoreType,
        appStoreData?.versionReviewSubmissionItem?.state,
        appStoreData?.relevantReviewSubmission?.state,
        null,
        null,
        null,
    );

    const isAppBuildSubmissionBlocked = appStoreData?.relevantReviewSubmission?.isAppBuildSubmissionBlocked;
    const unblockState = appStoreData?.relevantReviewSubmission?.unblockState;
    const shouldShowUnblockTag = isAppBuildSubmissionBlocked && (!!unblockState || unblockReviewSubmissionIsLoading);
    const shouldShowUnblockTooltip = isAppBuildSubmissionBlocked && !shouldShowUnblockTag;
    const isUnblockButtonDisabled =
        unblockState === UnblockAppStoreReviewSubmissionState.Unblocking ||
        unblockState === UnblockAppStoreReviewSubmissionState.Error;
    const isStepInactive = stepStatus == ReleaseStepStatus.Inactive;
    const isWorkflowsMigratedAndEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsMigratedAndEnabled(state, appId),
    );
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const isSubmissionEnabled = !!workflowEnabledSteps[ReleaseStepType.Submission];
    const shouldHideActions = isWorkflowsMigratedAndEnabled && isSubmissionEnabled;

    const handleOnUnblockClicked = () => {
        if (stepRunComponentProps) {
            return unblockReviewSubmissionStepRun();
        }

        const request: BaseUpdateReleaseRequest = {
            releaseId,
            appId,
            orgId,
        };
        dispatch(tryUpdateRelease(ReleaseUpdatable.UnblockReleaseReviewSubmission, request, releaseId));
    };

    React.useEffect(() => {
        if (unblockReviewSubmissionError) {
            globalMessage.error(unblockReviewSubmissionError);
        }
    }, [unblockReviewSubmissionError]);

    React.useEffect(() => {
        if (unblockReviewSubmissionIsLoading) {
            globalMessage.info(
                `Runway is now in the process of unblocking your ${releaseDisplayableVersion} submission. Check back here for status.`,
            );
        }
    }, [unblockReviewSubmissionIsLoading]);

    const buildAndReviewSettingsCard = (
        <BuildAndReviewSettingsCard
            stepRunComponentProps={stepRunComponentProps}
            orgId={orgId}
            appId={appId}
            releaseId={releaseId}
            releaseStepType={releaseStepType}
            appStoreData={appStoreData}
            googleStoreData={null}
            huaweiStoreData={null}
            samsungStoreData={null}
            appStoreType={appStoreType}
            platform={platform}
            autoStagedRolloutAutomationId={null}
            appStoreReleaseSettings={appStoreReleaseSettings}
            isRollback={isRollback}
            onShowAppStoreReleaseSettings={() => setEditReleaseSettingsModalVisible(true)}
            storeUrl={storeUrl}
            isReleased={isReleased}
            lastUploadedBuild={lastUploadedBuild}
            regressionBuildInfo={regressionBuildInfo}
            regressionStatus={regressionStatus}
            appStoreAvailableBuilds={appStoreAvailableBuilds}
            appStoreSelectedBuilds={appStoreSelectedBuilds}
            additionalAppStoreSelectedBuilds={additionalAppStoreSelectedBuilds}
            latestSuccessfulProdBuild={latestSuccessfulProdBuild}
            latestSuccessfulBuild={latestSuccessfulBuild}
            selectedStagingBuild={selectedStagingBuild}
            latestPassingRegressionTestBuild={latestPassingRegressionTestBuild}
            buildMatchingWarnings={buildMatchingWarnings}
        />
    );

    const unblockTag = shouldShowUnblockTag && (
        <Tooltip
            title={unblockTagTooltipTitle(releaseDisplayableVersion, unblockState, unblockReviewSubmissionIsLoading)}
        >
            <Tag
                style={{
                    ...titleStyle,
                    fontSize: 11,
                    lineHeight: '22px',
                    fontWeight: 700,
                    color: unblockStatusColor(unblockState, unblockReviewSubmissionIsLoading),
                    borderColor: unblockStatusColor(unblockState, unblockReviewSubmissionIsLoading),
                    marginLeft: 8,
                }}
                color={runwayColors.white}
            >
                {unblockStatusTitle(unblockState, unblockReviewSubmissionIsLoading)}
            </Tag>
        </Tooltip>
    );

    const unblockButton = isAppBuildSubmissionBlocked && !shouldHideActions && (
        <PermissionedActionButton
            releaseId={releaseId}
            actionType={RunwayUserAction.UAUpdateAppStoreReviewSubmission}
            icon={<AppstoreAddOutlined />}
            type="primary"
            loading={unblockReviewSubmissionIsLoading}
            disabled={isUnblockButtonDisabled}
            onClick={handleOnUnblockClicked}
        >
            {unblockButtonTitle(releaseDisplayableVersion, unblockState)}
        </PermissionedActionButton>
    );

    const unblockTooltip = shouldShowUnblockTooltip && (
        <Tooltip
            title={`The Unblock action will pull the current submission from review and add its items to your ${releaseDisplayableVersion} submission. Product optimizations won't be added to the new submission.`}
        >
            <InfoCircleOutlined style={{ color: runwayColors.info }} />
        </Tooltip>
    );

    const addItemButton = isIosPlatform(platform) &&
        !isAppBuildSubmissionBlocked &&
        !isSubmitted &&
        !shouldHideActions &&
        !isStepInactive && (
            <Button
                icon={
                    <PlusOutlined
                        style={{
                            color: runwayColors.primary,
                        }}
                    />
                }
                onClick={() => {
                    setIsAddReviewSubmissionItemsModalVisible(true);
                }}
            >
                Add an item
            </Button>
        );

    const viewInAppStoreConnectButton = ASCLink && (
        <Button
            icon={
                <ArrowRightOutlined
                    style={{
                        color: runwayColors.primary,
                    }}
                />
            }
            target={'_blank'}
            href={ASCLink}
        >
            View in App Store Connect
        </Button>
    );

    const defaultReviewSubmissionTitle = (
        <Text
            style={{ fontSize: 16, fontWeight: 400, color: antGrey.grey8 }}
        >{`${releaseDisplayableVersion} Submission`}</Text>
    );

    const reviewSubmissionTitle =
        isAppBuildSubmissionBlocked ?
            <Text
                style={{
                    fontSize: 16,
                    fontWeight: 400,
                    color: runwayColors.statusRed,
                }}
            >{`Items blocking ${releaseDisplayableVersion} submission`}</Text>
        :   defaultReviewSubmissionTitle;

    const shouldDisableRemoveItems = isSubmitted;

    const doesRelevantReviewSubmissionHasUnresolvedIssues =
        appStoreData?.relevantReviewSubmission?.state === AppStoreReviewSubmissionState.UnresolvedIssues;

    const isReleaseReleased = useSelector((state: RootState) => !!selectReleasedAt(state, releaseId));

    const hasReviewSubmission = !!appStoreData?.relevantReviewSubmission;

    const shouldHideReviewSubmissionInterfaces = isReleaseReleased && !hasReviewSubmission; // Hide the review submission interfaces if the release is released and there is no review submission data.

    const filteredReviewSubmissionItems = appStoreData?.relevantReviewSubmission?.reviewSubmissionItems?.filter(
        (item) => item.state !== AppStoreReviewSubmissionItemState.Removed,
    );

    const alreadyIncludedItemIdsMap =
        filteredReviewSubmissionItems?.reduce((acc, item) => {
            acc[item.itemId] = true;
            return acc;
        }, {}) ?? {};

    return (
        <div>
            {!shouldHideReviewSubmissionInterfaces && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {reviewSubmissionTitle}
                    <Space
                        style={{
                            marginBottom: 12,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignSelf: 'stretch',
                        }}
                    >
                        {viewInAppStoreConnectButton}
                        {addItemButton}
                        {unblockButton}
                        {unblockTag}
                        {unblockTooltip}
                    </Space>
                </div>
            )}

            <AppStoreSubmissionCollapse
                reviewSubmission={appStoreData?.relevantReviewSubmission}
                integrationId={IntegrationType.Apple}
                selectedBuilds={isAppBuildSubmissionBlocked ? [] : selectedBuilds}
                isAppVersionInsideReviewSubmission={isAppVersionInsideReviewSubmission}
                shouldHideReviewSubmissionInterfaces={shouldHideReviewSubmissionInterfaces}
                shouldHideSubmissionStatusTag={true}
            >
                {!isAppBuildSubmissionBlocked && buildAndReviewSettingsCard}
                {filteredReviewSubmissionItems?.map((reviewSubmissionItem) => {
                    return renderReviewsSubmissionItemCard({
                        item: reviewSubmissionItem.item,
                        type: reviewSubmissionItem.type,
                        reviewSubmissionItemId: reviewSubmissionItem.id,

                        removeButtonText:
                            doesRelevantReviewSubmissionHasUnresolvedIssues || isAppBuildSubmissionBlocked ? null : (
                                `Remove from ${releaseDisplayableVersion} submission`
                            ),
                        orgId,
                        appId,
                        releaseId,
                        isRemoveButtonDisabled: shouldDisableRemoveItems,
                        stepRunComponentProps,
                    });
                })}
            </AppStoreSubmissionCollapse>
            {isAppBuildSubmissionBlocked && (
                <div style={{ marginTop: 36, display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                    <Text
                        style={{ fontSize: 14, fontWeight: 400, color: antGrey.grey7 }}
                    >{`${releaseDisplayableVersion} submission`}</Text>
                    <AppStoreSubmissionCollapse
                        integrationId={IntegrationType.Apple}
                        selectedBuilds={selectedBuilds}
                        isAppVersionInsideReviewSubmission={false}
                        isReviewSubmissionBlocked={true}
                    >
                        {buildAndReviewSettingsCard}
                    </AppStoreSubmissionCollapse>
                </div>
            )}
            <AddReviewSubmissionItemsModal
                stepRunComponentProps={stepRunComponentProps}
                alreadyIncludedItemIdsMap={alreadyIncludedItemIdsMap}
                displayableVersion={displayableVersion}
                appStoreData={appStoreData}
                isLoading={addReviewSubmissionItemIsLoading}
                isOpen={isAddReviewSubmissionItemsModalVisible}
                onClose={() => {
                    setIsAddReviewSubmissionItemsModalVisible(false);
                }}
                onAddItem={(itemId, category) => {
                    addReviewSubmissionItem(itemId, category);
                }}
            />
        </div>
    );
};

const unblockButtonTitle = (
    releaseDisplayableVersion: string,
    unblockState: UnblockAppStoreReviewSubmissionState,
): string => {
    if (unblockState === UnblockAppStoreReviewSubmissionState.Unblocking) {
        return `Unblocking ${releaseDisplayableVersion} submission`;
    } else if (unblockState === UnblockAppStoreReviewSubmissionState.Error) {
        return `Error unblocking ${releaseDisplayableVersion} submission`;
    }

    return `Unblock ${releaseDisplayableVersion} submission`;
};

const unblockStatusTitle = (unblockState: UnblockAppStoreReviewSubmissionState, isLoading): string => {
    if (isLoading) {
        return 'IN PROGRESS';
    }
    switch (unblockState) {
        case UnblockAppStoreReviewSubmissionState.Unblocking:
            return 'IN PROGRESS';
        case UnblockAppStoreReviewSubmissionState.Error:
            return 'FAILED';
        default:
            return '';
    }
};

const unblockStatusColor = (unblockState: UnblockAppStoreReviewSubmissionState, isLoading): string => {
    if (isLoading) {
        return runwayColors.statusYellow;
    }

    switch (unblockState) {
        case UnblockAppStoreReviewSubmissionState.Unblocking:
            return runwayColors.statusYellow;
        case UnblockAppStoreReviewSubmissionState.Error:
            return runwayColors.statusRed;
        default:
            return runwayColors.white;
    }
};

const unblockTagTooltipTitle = (
    releaseDisplayableVersion: string,
    unblockState: UnblockAppStoreReviewSubmissionState,
    isLoading: boolean,
): string => {
    if (isLoading) {
        return `Runway is performing actions to unblock your submission (pulling the current submission from review, and adding its items to your ${releaseDisplayableVersion} submission). This can take between 2-10 minutes.`;
    }
    switch (unblockState) {
        case UnblockAppStoreReviewSubmissionState.Unblocking:
            return `Runway is performing actions to unblock your submission (pulling the current submission from review, and adding its items to your ${releaseDisplayableVersion} submission). This can take between 2-10 minutes.`;
        case UnblockAppStoreReviewSubmissionState.Error:
            return `Runway was unable to cancel the submission in progress that is blocking your ${releaseDisplayableVersion} release. View this submission in App Store Connect to resolve manually.`;
        default:
            return '';
    }
};
